import { ReadableApexAccountRegistrationType } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    FruitfulCustomIcon,
    FullScreenSpinner,
    InstitutionLogo,
    Separator,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { SelectableRow } from '../../../../../bills/components/common/BillRows';
import { useAuthedUser } from '../../../../../common/providers/AuthedUserProvider';
import { formatMaskedValue } from '../../../../../common/utils/bankingUtils';
import { useInternalInvestmentAccountsForIncomeSplit } from '../../../../hooks/income-split/useInternalInvestmentAccountsForIncomeSplit';
import useGetAllBankAccountDetailsFromGlobalState from '../../../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../../../hooks/useGetInternalAndExternalDepositAccounts';
import { selectedIncomeRuleAtom } from '../../../../state/PaycheckRoutingAtoms';
import type { BankAccountDisplayDetail } from '../../../../types/BankAccount';
import { PaycheckRoutingRuleAccountType } from '../../../../types/PaycheckRouting';
import IncomeSplitSubscreenWrapper from './IncomeSplitSubscreenWrapper';

const EditAccount = () => {
    const user = useAuthedUser();
    const { bankAccountDetails } = useGetAllBankAccountDetailsFromGlobalState();
    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});
    const {
        internalInvestmentAccounts,
        isLoading: isLoadingInternalInvestmentAccounts,
    } = useInternalInvestmentAccountsForIncomeSplit();
    const [selectedRule, setSelectedRule] = useAtom(selectedIncomeRuleAtom);

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );
    const prevAccountId = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (prevAccountId.current !== undefined) {
            // new account selected, move back
            router.back();
        } else {
            prevAccountId.current = selectedRule?.accountId ?? null;
        }
    }, [selectedRule?.accountId]);

    if (isLoadingDepositAccounts || isLoadingInternalInvestmentAccounts) {
        return <FullScreenSpinner />;
    }

    if (!selectedRule) {
        return <Redirect href="/" />;
    }

    return (
        <IncomeSplitSubscreenWrapper header="Account">
            <YStack separator={<Separator />}>
                {[
                    ...bankAccountDetails.filter(
                        (account) =>
                            account.primaryUser.id === user.id ||
                            account.jointUser?.id === user.id,
                    ),
                    ...externalAccounts,
                    ...(internalInvestmentAccounts ?? []).map((account) => ({
                        ...account,
                        isInternalInvestment: true,
                    })),
                ].map((accountDetails, i) => {
                    const { icon, color, nickname } = accountDetails;

                    const isExternal =
                        'isExternal' in accountDetails &&
                        accountDetails?.isExternal;
                    const isInternalInvestment =
                        'isInternalInvestment' in accountDetails &&
                        accountDetails?.isInternalInvestment;
                    const accountId = isExternal
                        ? accountDetails.id
                        : isInternalInvestment
                          ? (accountDetails as { apexAccountId: string })
                                .apexAccountId
                          : (accountDetails as BankAccountDisplayDetail)
                                .bankAccountId;
                    const isSelected = selectedRule.accountId === accountId;

                    return (
                        <View paddingVertical={8} key={i}>
                            <SelectableRow
                                isSelected={isSelected}
                                isMultiSelect={false}
                                isFirstRow={i === 0}
                                onSelect={() => {
                                    if (isSelected) {
                                        router.back();
                                    } else {
                                        setSelectedRule({
                                            ...selectedRule,
                                            accountId,
                                            isExternal: isExternal ?? false,
                                            targetBalanceCents: 0,
                                            accountType: isExternal
                                                ? PaycheckRoutingRuleAccountType.EXTERNAL
                                                : isInternalInvestment
                                                  ? PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
                                                  : PaycheckRoutingRuleAccountType.INTERNAL_DEPOSITORY,
                                        });
                                    }
                                }}
                                rowContentComponent={
                                    <XStack alignItems="center" gap={16}>
                                        <XStack
                                            minWidth={50}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            {isExternal ? (
                                                <InstitutionLogo
                                                    logo={
                                                        accountDetails.logo ??
                                                        accountDetails.imgBase64
                                                    }
                                                />
                                            ) : (
                                                <FruitfulCustomIcon
                                                    size="md"
                                                    icon={icon}
                                                    color={color}
                                                    useAltColors={
                                                        isInternalInvestment
                                                    }
                                                />
                                            )}
                                        </XStack>
                                        <YStack gap={4}>
                                            <BodyMediumRegular>
                                                {isExternal
                                                    ? accountDetails.accountType
                                                    : nickname}
                                            </BodyMediumRegular>
                                            {isExternal ? (
                                                <BodySmallRegular color="$gray1000">
                                                    {accountDetails.bankName} *
                                                    {accountDetails.accountNumberMasked?.slice(
                                                        -5,
                                                    )}
                                                </BodySmallRegular>
                                            ) : isInternalInvestment ? (
                                                <BodySmallRegular color="$gray1000">
                                                    {
                                                        ReadableApexAccountRegistrationType[
                                                            accountDetails
                                                                .registrationType
                                                        ]
                                                    }{' '}
                                                    {formatMaskedValue(
                                                        accountDetails.accountNumber.slice(
                                                            -4,
                                                        ),
                                                    )}
                                                </BodySmallRegular>
                                            ) : (
                                                <BodySmallRegular color="$gray1000">
                                                    Fruitful{' '}
                                                    {'accountNumberMasked' in
                                                        accountDetails &&
                                                    accountDetails.accountNumberMasked !=
                                                        null
                                                        ? accountDetails.accountNumberMasked.slice(
                                                              -5,
                                                          )
                                                        : ''}
                                                </BodySmallRegular>
                                            )}
                                        </YStack>
                                    </XStack>
                                }
                            />
                        </View>
                    );
                })}
            </YStack>
        </IncomeSplitSubscreenWrapper>
    );
};

export default EditAccount;
